import { v4 as uuidv4 } from "uuid";
import { getPhotoDuplicatePairs, normalizePastedProductServicePhotos } from "./productServicePhotos";

const withNormalizedPhotos = (ps) => normalizePastedProductServicePhotos(ps);

function generateNewIds(ps, newDocumentItemId) {
  const newPsId = uuidv4();
  let childrenIdReference = {};
  let newPs = {};
  if (ps?.is_bundle) {
    childrenIdReference = ps?.children?.reduce((acc, current) => {
      acc[current.document_item_product_service_id] = uuidv4();
      return acc;
    }, {});

    newPs = withNormalizedPhotos({
      ...ps,
      document_item_product_service_id: newPsId,
      document_item_id: newDocumentItemId,
      product_service_id: uuidv4(),
      bundle_group: {
        items: ps?.bundle_group?.items.map((id) => childrenIdReference[id]) || [],
        default_group_ps_item_id: childrenIdReference[ps?.bundle_group?.default_group_ps_item_id] || ""
      },
      children:
        ps?.children?.map((item) => {
          return withNormalizedPhotos({
            ...item,
            document_item_product_service_id: childrenIdReference[item.document_item_product_service_id],
            product_service_parent_id: [newPsId],
            product_service_id: uuidv4(),
            document_item_id: newDocumentItemId
          });
        }) || []
    });
    return {
      newPs,
      childrenIdReference,
      oldPs: ps
    };
  }
  if (ps.is_inside_bundle) {
    newPs = withNormalizedPhotos({
      ...ps,
      document_item_product_service_id: newPsId,
      document_item_id: newDocumentItemId,
      product_service_id: uuidv4(),
      is_inside_bundle: false,
      total: Number(ps.sell || 0) * Number(ps.quantity || 1) - Number(ps.discount || 0)
    });
    return {
      newPs,
      childrenIdReference,
      oldPs: ps
    };
  }
  newPs = withNormalizedPhotos({
    ...ps,
    document_item_product_service_id: newPsId,
    product_service_id: uuidv4(),
    document_item_id: newDocumentItemId
  });

  return {
    newPs,
    childrenIdReference,
    oldPs: ps
  };
}

// Combined "Short/Long Description" column: shows the short description with the long
// description stacked on the line beneath it. Available as a toggleable column alongside the
// plain "Description" column.
export const SHORT_LONG_DESCRIPTION_COLUMN = {
  position: 2.5,
  visible: false,
  name: "short_long_description",
  header: "Description",
  defaultFlex: 1,
  minWidth: 70,
  defaultWidth: 210.93
};

// Ensure a product/service column config contains the Short/Long Description column. Existing
// documents persisted their column list before this column existed, so inject it (hidden) right
// after the short_description column whenever it's missing. Idempotent.
export const ensureShortLongDescriptionColumn = (tableData) => {
  if (!Array.isArray(tableData)) return tableData;
  if (tableData.some((col) => col?.name === "short_long_description")) return tableData;
  const insertAfter = tableData.findIndex((col) => col?.name === "short_description");
  const column = { ...SHORT_LONG_DESCRIPTION_COLUMN };
  if (insertAfter === -1) return [...tableData, column];
  const next = [...tableData];
  next.splice(insertAfter + 1, 0, column);
  return next;
};

export const adjustWidthsProportionally = (items, deduct = true, changedWidth) => {
  const total = items.reduce((sum, item) => sum + Number(item.defaultWidth), 0);

  if (total === 0) return items.map((item) => ({ ...item, width: Number(item.defaultWidth) }));

  return items.map((item) => {
    const proportion = Number(item.defaultWidth) / total;
    const adjustment = proportion * changedWidth;

    return {
      ...item,
      defaultWidth: deduct ? Number(item.defaultWidth) - adjustment : Number(item.defaultWidth) + adjustment
    };
  });
};

const pushPhotoDuplicatePairs = (acc, oldPs, newPs, generateProductServiceUrl, tenantId) => {
  if (tenantId) {
    getPhotoDuplicatePairs(oldPs, newPs, tenantId).forEach((pair) => acc.push(pair));
    return acc;
  }

  const from = generateProductServiceUrl(oldPs);
  if (from) {
    acc.push({
      from,
      to: generateProductServiceUrl(newPs)
    });
  }

  return acc;
};

export function getDuplicateProductServicesMedia(productServices, generateProductServiceUrl, tenantId) {
  return productServices
    .reduce((acc, current) => {
      if (current?.oldPs?.is_bundle && current?.oldPs?.children?.length) {
        current.oldPs.children.forEach((oldChild) => {
          const newChild = current.newPs.children.find(
            (child) =>
              current.childrenIdReference[oldChild.document_item_product_service_id] ===
              child.document_item_product_service_id
          );
          if (newChild) {
            pushPhotoDuplicatePairs(acc, oldChild, newChild, generateProductServiceUrl, tenantId);
          }
        });
      }

      pushPhotoDuplicatePairs(acc, current.oldPs, current.newPs, generateProductServiceUrl, tenantId);
      return acc;
    }, [])
    .filter((psWithPhoto) => Boolean(psWithPhoto?.from));
}

export function getMaxProductServicePosition(items = []) {
  if (!items?.length) return 0;
  return Math.max(0, ...items.map((item) => Number(item?.position) || 0));
}

export function getNextTopLevelProductServicePosition(productServices = [], documentItemId) {
  const topLevel = productServices.filter(
    (ps) =>
      !ps?.is_inside_bundle &&
      (documentItemId == null || String(ps.document_item_id) === String(documentItemId))
  );
  return getMaxProductServicePosition(topLevel) + 1;
}

export function getNextBundleChildPosition(parent) {
  return getMaxProductServicePosition(parent?.children) + 1;
}

/** defaultPS uses position 0; treat non-positive values as unset. */
export function resolveProductServicePosition(explicitPosition, fallbackPosition) {
  const n = Number(explicitPosition);
  if (Number.isFinite(n) && n > 0) return n;
  return fallbackPosition;
}

export const DOCUMENT_ORIGIN_ID = {
  LEGACY: 0,
  INTERNAL: 1,
  EXTERNAL: 2,
  CUSTOM: 3
};

/**
 * Resolves document_origin_id for a PS item at creation/save time.
 * - Edit of existing item: preserves the current origin
 * - New from internal search: 1
 * - New from external product feed: 2
 * - New custom item (no search, bulk insert, PDF): 3
 */
export function resolveDocumentOriginId({ isNew, isCreatingFromSearch, isInternalSearch, existingOriginId }) {
  if (!isNew && existingOriginId != null) return existingOriginId;
  if (isCreatingFromSearch) return isInternalSearch ? DOCUMENT_ORIGIN_ID.INTERNAL : DOCUMENT_ORIGIN_ID.EXTERNAL;
  return DOCUMENT_ORIGIN_ID.CUSTOM;
}

export function shouldNotifyIntegrationOnProductAdd(documentOriginId) {
  return Number(documentOriginId) > 1;
}

export function buildIntegrationProductAddMessage(integrationName) {
  return `Products/Services may be added to ${integrationName}. Please make sure all information is correct.`;
}

export const defaultPS = {
  category: "",
  category_id: null,
  cost: 0,
  currency_id: 840,
  discount: 0,
  document_item_id: null,
  document_item_product_service_id: null,
  document_item_product_service_parent_id: null,
  document_origin_id: DOCUMENT_ORIGIN_ID.LEGACY,
  include_in_adjustment: true,
  location: null,
  long_description: null,
  manual: null,
  manufacturer: null,
  manufacturer_id: null,
  manufacturer_part_number: null,
  map: "",
  msrp: "",
  optional: false,
  optional_is_selected: false,
  photo: null,
  position: 0,
  quantity: "",
  quantity_adjustment_allowed: false,
  recurrence_id: null,
  recurrence_include_in_total: true,
  recurrence_option_id: 1,
  sell: 0,
  short_description: "",
  specification_sheet: null,
  sub_category: null,
  sub_category_id: null,
  taxable: true,
  tenant_id: null,
  tenant_part_number: null,
  total: 0,
  type: "",
  type_id: null,
  uom: "",
  uom_id: null,
  vendor_id: null,
  vendor_name: null
};

export default generateNewIds;
